import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import DefaultTooltip from "../../components/tool_tips/DefaultTooltip";
import ActionButtons from "../../utils/ActionButtons";
import "../../css/components/reusable/DynamicTable.css";

const DynamicTable = ({ columns, data, itemsPerPage = 10, onAction }) => {
    const [sortKey, setSortKey] = useState(columns[0]?.key || "");
    const [sortOrder, setSortOrder] = useState("asc");
    const [page, setPage] = useState(1);

    // 🔍 SORTING
    const sortedData = useMemo(() => {
        if (!sortKey) return data;
        return [...data].sort((a, b) => {
            let valA = a[sortKey];
            let valB = b[sortKey];

            if (valA instanceof Date || !isNaN(Date.parse(valA))) {
                valA = new Date(valA);
                valB = new Date(valB);
            }

            if (valA < valB) return sortOrder === "asc" ? -1 : 1;
            if (valA > valB) return sortOrder === "asc" ? 1 : -1;
            return 0;
        });
    }, [data, sortKey, sortOrder]);

    // 📄 PAGINATION
    const totalPages = Math.ceil(sortedData.length / itemsPerPage);
    const paginatedData = sortedData.slice(
        (page - 1) * itemsPerPage,
        page * itemsPerPage
    );

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortKey(key);
            setSortOrder("asc");
        }
    };

    return (
        <div className="table-card">
            <div className="table-wrapper">
                <table className="activity-table">
                    <thead>
                        <tr>
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    onClick={() => col.sortable && handleSort(col.key)}
                                >
                                    {col.tooltip ? (
                                        <DefaultTooltip text={col.tooltip}>{col.label}</DefaultTooltip>
                                    ) : (
                                        col.label
                                    )}
                                </th>
                            ))}
                            {onAction && <th>ACTIONS</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.map((row, index) => (
                            <tr key={index}>
                                {columns.map((col, colIndex) => {
                                    // Auto-serial number column
                                    if (col.key === "__sn") {
                                        return <td key={colIndex}>
                                            {(page - 1) * itemsPerPage + index + 1}
                                        </td>;
                                    }
                                    return <td key={colIndex}>
                                        {col.render ? col.render(row[col.key], row) : row[col.key]}
                                    </td>;
                                })}
                                {onAction && (
                                    <td className="actions">
                                        {onAction.map((action, idx) => (
                                            // <button
                                            //     key={idx}
                                            //     className={`action-icon-btn ${action.className || ""}`}
                                            //     title={action.title}
                                            //     onClick={() => action.onClick(row)}
                                            // >
                                            //     {action.icon || action.label}
                                            // </button>
                                            <ActionButtons
                                                key     = { idx }
                                                actions = { [
                                                    {
                                                        buttonType: action.buttonType ? action.buttonType : "view",
                                                        customName: action.icon || action.label,
                                                        onClick: () => action.onClick?.(row),
                                                        dropdownOptions: action.getDropdownOptions
                                                            ? action.getDropdownOptions(row)
                                                            : action.dropdownOptions,
                                                    }
                                                ]}
                                            />
                                        ))}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* PAGINATION */}
            <div className="table-footer">
                <span>
                    Showing {(page - 1) * itemsPerPage + 1}–
                    {Math.min(page * itemsPerPage, sortedData.length)} of {sortedData.length} entries
                </span>

                <div className="pagination">
                    <button disabled={page === 1} onClick={() => setPage(page - 1)}>
                        {"<"}
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => (
                        <button
                            key={i}
                            className={page === i + 1 ? "active" : ""}
                            onClick={() => setPage(i + 1)}
                        >
                            {i + 1}
                        </button>
                    ))}

                    <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>
                        {">"}
                    </button>
                </div>
            </div>
        </div>
    );
};

DynamicTable.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            tooltip: PropTypes.string,
            sortable: PropTypes.bool,
            render: PropTypes.func, // custom cell rendering
        })
    ).isRequired,
    data: PropTypes.array.isRequired,
    itemsPerPage: PropTypes.number,
    onAction: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            icon: PropTypes.element,
            title: PropTypes.string,
            className: PropTypes.string,
            onClick: PropTypes.func.isRequired,
        })
    ),
};

export default DynamicTable;
